import 'dayjs/locale/es';

import { Box, Dialog, DialogContent, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { createSGRWarrantyRequest } from 'lib/api/sgr';
import { CreateForm } from 'lib/api/sgr/types';
import CreateWarrantyForm from 'lib/common/Forms/Warranty/BackOffice/Create';
import { SGR_TYPES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Calculator from '../Calculator';
import { InitialValues } from '../Summary';
import ConfirmationDialog from './Components/ConfirmationDialog';

dayjs.locale('es');

interface Props {
  open: boolean;
  onClose: () => void;
  fetchWarranties: () => void;
  defaultValues?: InitialValues;
}

const CreateWarrantyDialog: React.FC<Props> = ({
  open,
  onClose,
  fetchWarranties,
  defaultValues,
}) => {
  const initialValues = useMemo(() => {
    return {
      businessIdentifier: defaultValues?.businessIdentifier || null,
      tenderId: defaultValues?.tenderId || null,
      quotedAmount: defaultValues?.quotedAmount || 0,
      quotedCurrency: defaultValues?.quotedCurrency || 'CLP',
      type: defaultValues?.type || SGR_TYPES[0].value,
      principalRut: defaultValues?.principalRut || null,
      unitCode: defaultValues?.unitCode || null,
      startDate: dayjs(defaultValues?.startDate),
      endDate: dayjs(defaultValues?.endDate),
      description: defaultValues?.description || null,
      fogape: defaultValues?.fogape || null,
    };
  }, [defaultValues]);
  const dispatch = useDispatch();
  const [form, setForm] = useState<CreateForm>(initialValues);
  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: defaultValues?.variableRate || null,
    fixedRate: defaultValues?.fixedRate || null,
    variableDiscount: defaultValues?.variableDiscount || null,
    fixedDiscount: defaultValues?.fixedDiscount || null,
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { mutate, isPending } = createSGRWarrantyRequest(dispatch);

  const handleConfirm = async () => {
    const data = {
      ...form,
      operationCostVariable: rateControls.variableRate,
      operationCostFixed: rateControls.fixedRate,
      discountRateVariable: rateControls.variableDiscount,
      discountRateFixed: rateControls.fixedDiscount,
    };
    mutate(data, {
      onSuccess: () => {
        onClose();
        fetchWarranties();
      },
    });
  };

  useEffect(() => {
    if (initialValues) setForm(initialValues);
  }, [initialValues, open]);

  const isFormValid = () => {
    return (
      form.type &&
      form.quotedAmount &&
      form.quotedCurrency &&
      form.startDate &&
      form.endDate &&
      form.principalRut &&
      form.businessIdentifier
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <CustomDialogTitle setOpen={onClose}>
            Nueva Garantía SGR
          </CustomDialogTitle>
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <CreateWarrantyForm form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={6}>
              <Calculator
                amount={form.quotedAmount}
                startDate={form.startDate}
                endDate={form.endDate}
                quotedCurrency={form.quotedCurrency}
                type={form.type}
                businessIdentifier={form.businessIdentifier}
                rateControls={rateControls}
                setRateControls={setRateControls}
                useConcentrationRates={true}
                canEditRates={true}
                isCreating={true}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <CustomButton color="secondary" onClick={onClose}>
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={() => setOpenConfirmation(true)}
            disabled={!isFormValid()}
          >
            Crear Garantía
          </CustomButton>
        </Box>
      </Dialog>
      {openConfirmation && (
        <ConfirmationDialog
          open={openConfirmation}
          form={form}
          rateControls={rateControls}
          isPending={isPending}
          onClose={() => setOpenConfirmation(false)}
          handleConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default CreateWarrantyDialog;
